import React, { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { buildLoginUrl } from "@/services/authService";
import { TriangleAlert } from "lucide-react";

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: "ADMIN" | "MANAGER" | "ACCOUNTANT" | "CLIENT";
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Показываем загрузчик пока проверяем аутентификацию
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-hover/50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-ink-2">Проверка аутентификации...</p>
        </div>
      </div>
    );
  }

  // Если не авторизован — редирект на страницу входа с запоминанием текущего адреса,
  // чтобы после входа пользователь вернулся туда, где его прервали
  if (!isAuthenticated) {
    const target = `${location.pathname}${location.search}`;
    return <Navigate to={buildLoginUrl(target)} state={{ from: location }} replace />;
  }

  // Проверяем права доступа, если требуется определенная роль
  if (requiredRole && user) {
    const hasPermission = checkPermission(user.role, requiredRole);

    if (!hasPermission) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-surface-hover/50">
          <div className="max-w-md w-full bg-surface-elevated shadow-lg rounded-lg p-8 text-center">
            <TriangleAlert className="mx-auto mb-4 h-10 w-10 text-risk-d" strokeWidth={1.5} aria-hidden />
            <h2 className="text-2xl font-bold text-ink-1 mb-4">Недостаточно прав доступа</h2>
            <p className="text-ink-2 mb-6">
              Для доступа к данной странице требуется роль <span className="font-semibold">{requiredRole}</span>.
              <br />
              Ваша текущая роль: <span className="font-semibold">{user.role}</span>
            </p>
            <div className="text-sm text-ink-3">
              Обратитесь к администратору для получения необходимых прав доступа.
            </div>
          </div>
        </div>
      );
    }
  }

  // Пользователь аутентифицирован и имеет необходимые права
  return <>{children}</>;
};

/**
 * Проверка прав доступа с иерархией ролей
 * ADMIN > MANAGER > ACCOUNTANT > CLIENT
 */
function checkPermission(
  userRole: "ADMIN" | "MANAGER" | "ACCOUNTANT" | "CLIENT",
  requiredRole: "ADMIN" | "MANAGER" | "ACCOUNTANT" | "CLIENT",
): boolean {
  // ADMIN имеет все права
  if (userRole === "ADMIN") return true;

  // Иерархия ролей
  const roleHierarchy = ["CLIENT", "ACCOUNTANT", "MANAGER", "ADMIN"];
  const userRoleLevel = roleHierarchy.indexOf(userRole);
  const requiredRoleLevel = roleHierarchy.indexOf(requiredRole);

  return userRoleLevel >= requiredRoleLevel;
}

export default ProtectedRoute;
